import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import {
  createSelectProjectByIdFillingUnknownOne,
  UNKNOWN_PROJECT_LABEL,
} from "../../../state/workspaces/projects.selector";
import type {
  Label,
  MyWorkspaceMetadata,
  Project,
} from "../../../state/workspaces/types";
import { selectProject } from "../../../state/workspaces/workspaces.actions";
import { isWorkspaceLimited } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import ProjectLabel from "./ProjectLabel";
import WorkspaceLabel from "./WorkspaceLabel";
import Styles from "./WorkspacePreviewLabels.module.css";

type WorkspacePreviewLabelsProps = {
  canEditLabels: boolean;
  myWorkspaceMetadata: MyWorkspaceMetadata;
  showEditLabelPrompt: () => void;
};

const WorkspacePreviewLabels = ({
  canEditLabels,
  myWorkspaceMetadata,
  showEditLabelPrompt,
}: WorkspacePreviewLabelsProps) => {
  const dispatch = useDispatch();

  const t = useI18n();

  const limitedLabelName = t("WORKSPACE_CARD.LIMITED_TITLE");
  const readOnlyLabelName = t("APP.READ_ONLY");

  const {
    containerId,
    labels,
    myPermissions: { canWrite },
    templateId,
    workspaceId,
  } = myWorkspaceMetadata;
  const isReadOnly = !canWrite;

  const isLimited = useSelector((state: RootState) =>
    isWorkspaceLimited(state, workspaceId),
  );

  const selectProjectByIdFillingUnknownOne = useMemo(
    () => createSelectProjectByIdFillingUnknownOne(),
    [],
  );
  const project = useSelector((state: RootState) =>
    selectProjectByIdFillingUnknownOne(state, containerId),
  );

  /*
  No Limited Label for project workspaces and templates. Project ws are either Active or Inactive.
  If Project is Expired (equal to Inactive) we set `canGrow` permission to `false`,
  however do nothing with styling with this WorkspaceCard.
  */
  const processedLabels = useMemo(() => {
    const resultLabels = labels ?? [];
    if (templateId) {
      return resultLabels;
    }

    const additionalLabel =
      isLimited && !project
        ? { name: limitedLabelName }
        : isReadOnly
          ? { name: readOnlyLabelName }
          : null;
    return additionalLabel ? [...resultLabels, additionalLabel] : resultLabels;
  }, [isLimited, isReadOnly, project, labels, templateId]);

  const onProjectClick = (e: React.MouseEvent, project: Project) => {
    if (project.name === UNKNOWN_PROJECT_LABEL) return;

    if (project.status === "Inactive") {
      window.open("https://www.hoylu.com/projects", "_blank");
      e.stopPropagation();
      return;
    }

    if (canEditLabels) {
      e.stopPropagation();
      dispatch(selectProject(project));
    }
  };
  const onLabelClick = (
    e: React.MouseEvent,
    isWarningLabel: boolean = false,
  ) => {
    if (isWarningLabel) {
      window.open("https://www.hoylu.com/plans", "_blank");
      e.stopPropagation();
      return;
    }

    if (canEditLabels) {
      e.stopPropagation();
      showEditLabelPrompt();
    }
  };

  return (
    <div className={Styles.workspacePreviewLabels}>
      {processedLabels.map((label: Label) => (
        <WorkspaceLabel
          isLimited={isLimited && label.name === limitedLabelName}
          isReadOnly={isReadOnly && label.name === readOnlyLabelName}
          key={label.name}
          label={label}
          onClick={onLabelClick}
        />
      ))}
      {project && (
        <ProjectLabel
          key={project.name}
          onClick={onProjectClick}
          project={project}
        />
      )}
    </div>
  );
};

export default WorkspacePreviewLabels;
